import React, { useState, useEffect, useCallback } from 'react';
import { useTransaction } from '../../shared/hooks';
import { Loader } from '../../shared/ui';
import { Header } from '../../shared/ui/layout';
import { TransactionDetails } from './TransactionDetails';
import { DocumentViewer } from './DocumentViewer';

/* ── Share button ─────────────────────────────────────────────── */
function ShareButton({ transaction, className = '' }) {
  const [status, setStatus] = useState('idle'); // idle | copied | unsupported

  const handleShare = useCallback(async () => {
    const company  = transaction?.sender?.companyName;
    const docType  = transaction?.transactionDetails?.transactionType;
    const entryNo  = transaction?.transactionDetails?.EntryNo;
    const title    = [company, docType, entryNo].filter(Boolean).join(' — ');
    const url      = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title, text: title, url });
      } catch {
        // User cancelled — not an error
      }
      return;
    }

    // Fallback: copy URL to clipboard
    try {
      await navigator.clipboard.writeText(url);
      setStatus('copied');
      setTimeout(() => setStatus('idle'), 2000);
    } catch {
      setStatus('unsupported');
      setTimeout(() => setStatus('idle'), 2500);
    }
  }, [transaction]);

  const label =
    status === 'copied'      ? 'Link copied!' :
    status === 'unsupported' ? 'Copy failed'  :
    'Share';

  const icon =
    status === 'copied'      ? 'bi-check2'         :
    status === 'unsupported' ? 'bi-exclamation'     :
    navigator.share          ? 'bi-share'           :
    'bi-link-45deg';

  return (
    <button
      type="button"
      className={`share-btn${status !== 'idle' ? ` share-btn--${status}` : ''} ${className}`.trim()}
      onClick={handleShare}
      aria-label={label}
      aria-live="polite"
    >
      <i className={`bi ${icon}`} aria-hidden="true"></i>
      <span>{label}</span>
    </button>
  );
}

/* ── Inline error / retry screen ─────────────────────────────── */
function FetchError({ message, onRetry }) {
  return (
    <div className="fetch-error" role="alert">
      <div className="fetch-error__inner">
        <i className="bi bi-wifi-off fetch-error__icon" aria-hidden="true"></i>
        <h2 className="fetch-error__title">Couldn't load document</h2>
        <p className="fetch-error__desc">{message || 'An unexpected error occurred.'}</p>
        <button
          type="button"
          className="doc-action-btn doc-action-btn--primary"
          onClick={onRetry}
        >
          <i className="bi bi-arrow-clockwise" aria-hidden="true"></i>
          Try again
        </button>
      </div>
    </div>
  );
}

/* ── Empty / skeleton state while transaction is null ─────────── */
function EmptyState() {
  return (
    <div className="app-shell">
      <div className="skeleton-header" aria-hidden="true" />
      <div className="desktop-layout">
        <aside className="desktop-sidebar">
          <div className="skeleton-sidebar" />
        </aside>
        <main className="desktop-main">
          <div className="skeleton-canvas" />
        </main>
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────── */
const TransactionPage = () => {
  const { transaction, loading, error, retry } = useTransaction();
  const [activeTab, setActiveTab] = useState('invoice');

  useEffect(() => {
    const company = transaction?.sender?.companyName;
    document.title = company ? `${company} — SwilShare` : 'SwilShare';
    return () => { document.title = 'SwilShare'; };
  }, [transaction]);

  if (loading) return <Loader />;
  if (error)   return <FetchError message={error} onRetry={retry} />;
  if (!transaction) return <EmptyState />;

  return (
    <div className="app-shell">
      <Header transaction={transaction} />

      {/* ── Desktop: fixed two-column layout ── */}
      <div className="desktop-layout">
        <aside className="desktop-sidebar" aria-label="Transaction details">
          <TransactionDetails transaction={transaction} />
        </aside>
        <main className="desktop-main" aria-label="Document viewer">
          <DocumentViewer transaction={transaction} />
        </main>
      </div>

      {/* ── Mobile: tabbed layout ── */}
      <div className="mobile-layout">
        <div
          className="mobile-tab-bar"
          role="tablist"
          aria-label="Document sections"
        >
          <button
            id="tab-invoice"
            role="tab"
            aria-selected={activeTab === 'invoice'}
            aria-controls="tabpanel-invoice"
            className={`mobile-tab-btn${activeTab === 'invoice' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('invoice')}
          >
            <i className="bi bi-file-earmark-pdf" aria-hidden="true"></i>
            Invoice
          </button>
          <button
            id="tab-details"
            role="tab"
            aria-selected={activeTab === 'details'}
            aria-controls="tabpanel-details"
            className={`mobile-tab-btn${activeTab === 'details' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            <i className="bi bi-list-ul" aria-hidden="true"></i>
            Details
          </button>

          {/* Share sits in the tab bar as a third action — not a tab */}
          <ShareButton
            transaction={transaction}
            className="mobile-tab-btn mobile-tab-btn--share"
          />
        </div>

        <div
          id="tabpanel-invoice"
          role="tabpanel"
          aria-labelledby="tab-invoice"
          className="mobile-tab-content"
          hidden={activeTab !== 'invoice'}
        >
          <DocumentViewer transaction={transaction} />
        </div>
        <div
          id="tabpanel-details"
          role="tabpanel"
          aria-labelledby="tab-details"
          className="mobile-tab-content"
          hidden={activeTab !== 'details'}
        >
          <TransactionDetails transaction={transaction} />
        </div>
      </div>
    </div>
  );
};

export default TransactionPage;
